import { useEffect, useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { formatBrazilPhone, isValidBrazilPhone } from "@/lib/inputFormatters";
import { showOperationSuccess } from "@/lib/operationFeedback";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Zap, User, Phone, Mail, Scissors, Clock, UserCheck, Loader2, AlertCircle } from "lucide-react";
import { useLocation } from "wouter";

interface WalkInModalProps {
  open: boolean;
  onClose: () => void;
  salonId?: number;
}

function formatLocalDateTimeInput(date: Date) {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function nextQuarterHour(offsetMinutes = 0) {
  const next = new Date();
  next.setMinutes(Math.ceil(next.getMinutes() / 15) * 15 + offsetMinutes, 0, 0);
  return formatLocalDateTimeInput(next);
}

export function WalkInModal({ open, onClose, salonId }: WalkInModalProps) {
  const [, navigate] = useLocation();
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [serviceId, setServiceId] = useState<string>("");
  const [professionalId, setProfessionalId] = useState<string>("");
  const [startTime, setStartTime] = useState(() => nextQuarterHour());
  const [notes, setNotes] = useState("");

  // Stable query inputs — objetos literais inline causam re-fetches infinitos
  const walkInServicesInput = useMemo(() => ({ salonId: salonId! }), [salonId]);
  const walkInProfsInput = useMemo(() => ({ salonId: salonId! }), [salonId]);
  const compatibleProfessionalsInput = useMemo(
    () => ({ salonId: salonId!, serviceIds: serviceId ? [Number(serviceId)] : [] }),
    [salonId, serviceId]
  );
  const { data: services, isLoading: servicesLoading, error: servicesError } = trpc.services.list.useQuery(
    walkInServicesInput,
    { enabled: !!salonId }
  );
  const { data: professionals, isLoading: professionalsLoading, error: professionalsError } = trpc.professionals.list.useQuery(
    walkInProfsInput,
    { enabled: !!salonId }
  );
  const {
    data: compatibleProfessionals = [],
    isLoading: compatibleProfessionalsLoading,
    error: compatibleProfessionalsError,
  } = trpc.professionals.listByServices.useQuery(
    compatibleProfessionalsInput,
    { enabled: !!salonId && !!serviceId }
  );
  const activeProfessionals = useMemo(
    () => professionals?.filter((professional) => professional.isActive !== false) ?? [],
    [professionals]
  );
  const eligibleProfessionals = useMemo(
    () => compatibleProfessionals.filter((professional) => professional.isActive !== false),
    [compatibleProfessionals]
  );
  const isPreparingOptions = servicesLoading || professionalsLoading;
  const setupError = servicesError || professionalsError || (serviceId ? compatibleProfessionalsError : null);
  const hasNoServices = !servicesLoading && !setupError && (services?.length ?? 0) === 0;
  const hasNoProfessionals = !professionalsLoading && !setupError && !hasNoServices && activeProfessionals.length === 0;
  const hasNoEligibleProfessionals = Boolean(serviceId) && !compatibleProfessionalsLoading && !setupError && eligibleProfessionals.length === 0;
  const hasUnsavedChanges = Boolean(clientName.trim() || clientPhone.trim() || clientEmail.trim() || serviceId || professionalId || notes.trim());

  useEffect(() => {
    if (!serviceId || compatibleProfessionalsLoading) return;
    if (professionalId && !eligibleProfessionals.some((professional) => String(professional.id) === professionalId)) {
      setProfessionalId("");
      return;
    }
    if (!professionalId && eligibleProfessionals.length === 1) {
      setProfessionalId(String(eligibleProfessionals[0].id));
    }
  }, [serviceId, compatibleProfessionalsLoading, eligibleProfessionals, professionalId]);

  const utils = trpc.useUtils();
  const trackExperience = trpc.experience.track.useMutation();
  const createWalkIn = trpc.walkIn.create.useMutation({
    onSuccess: (data) => {
      showOperationSuccess({
        title: "Atendimento criado",
        description: `${clientName.trim()} foi incluído na agenda e o pagamento pode ser registrado em seguida.`,
      });
      utils.appointments.getByDateRange.invalidate();
      utils.analytics.getSummary.invalidate();
      resetForm();
      onClose();
      if (data.appointmentId) {
        navigate(`/checkout?highlight=${data.appointmentId}`);
      }
    },
    onError: (err) => {
      toast.error("Erro ao criar atendimento", {
        description: err.message,
      });
    },
  });

  function resetForm() {
    setClientName("");
    setClientPhone("");
    setClientEmail("");
    setServiceId("");
    setProfessionalId("");
    setNotes("");
    setStartTime(nextQuarterHour());
  }

  useEffect(() => {
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges || createWalkIn.isPending) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [hasUnsavedChanges, createWalkIn.isPending]);

  function requestClose() {
    if (hasUnsavedChanges && !createWalkIn.isPending) {
      const confirmed = window.confirm("Há dados de atendimento em andamento. Deseja sair e descartar o preenchimento?");
      if (!confirmed) return;
    }
    resetForm();
    onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!clientName.trim()) {
      toast.error("Informe o nome do cliente");
      return;
    }
    const phone = formatBrazilPhone(clientPhone);
    const email = clientEmail.trim();
    if (!isValidBrazilPhone(phone)) {
      toast.error("Informe um telefone válido com DDD.");
      return;
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Informe um e-mail válido.");
      return;
    }
    if (!serviceId) {
      toast.error("Selecione um serviço");
      return;
    }
    if (!professionalId) {
      toast.error("Selecione um profissional");
      return;
    }
    trackExperience.mutate({ eventKey: "walk_in_started", surface: "header" });
    createWalkIn.mutate({
      clientName: clientName.trim(),
      clientPhone: phone,
      clientEmail: email || undefined,
      serviceId: Number(serviceId),
      professionalId: Number(professionalId),
      startTime: new Date(startTime),
      notes: notes.trim() || undefined,
    });
  }

  const selectedService = services?.find(s => s.id === Number(serviceId));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && requestClose()}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-lg max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center">
              <Zap className="w-4 h-4 text-primary" />
            </div>
            Atendimento Rápido de Balcão
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          {hasUnsavedChanges && <p className="rounded-lg bg-primary/5 px-3 py-2 text-xs font-medium text-primary">Preenchimento em andamento: o sistema pede confirmação antes de descartar os dados.</p>}
          {isPreparingOptions && <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/30 px-3 py-2.5 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin text-primary" />Preparando serviços e profissionais disponíveis...</div>}
          {setupError && <div className="rounded-lg border border-destructive/25 bg-destructive/5 p-3"><p className="flex items-center gap-2 text-sm font-medium text-destructive"><AlertCircle className="h-4 w-4" />Não foi possível carregar as opções.</p><p className="mt-1 text-xs text-muted-foreground">Feche e abra novamente. Se continuar, confira sua conexão.</p></div>}
          {hasNoServices && <div className="rounded-lg border border-amber-200 bg-amber-50 p-3"><p className="text-sm font-medium text-amber-800">Cadastre um serviço antes de iniciar um atendimento.</p><Button type="button" size="sm" variant="outline" className="mt-2 h-7 border-amber-300 text-amber-800" onClick={() => { requestClose(); navigate("/services"); }}>Cadastrar serviço</Button></div>}
          {hasNoProfessionals && <div className="rounded-lg border border-amber-200 bg-amber-50 p-3"><p className="text-sm font-medium text-amber-800">Cadastre um profissional ativo antes de iniciar um atendimento.</p><Button type="button" size="sm" variant="outline" className="mt-2 h-7 border-amber-300 text-amber-800" onClick={() => { requestClose(); navigate("/professionals"); }}>Cadastrar profissional</Button></div>}
          {hasNoEligibleProfessionals && <div className="rounded-lg border border-amber-200 bg-amber-50 p-3"><p className="text-sm font-medium text-amber-800">Nenhum profissional está habilitado para este serviço.</p><p className="mt-1 text-xs text-amber-700">Peça ao administrador para vinculá-lo em Profissionais &gt; Serviços.</p></div>}
          {/* Seção: Dados do Cliente */}
          <div className="rounded-lg border border-border/60 bg-muted/30 p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Dados do Cliente
            </p>

            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5 text-xs font-medium">
                <User className="w-3 h-3" /> Nome *
              </Label>
              <Input
                placeholder="Ex: Maria Silva"
                value={clientName}
                onChange={e => setClientName(e.target.value)}
                autoFocus
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5 text-xs font-medium">
                  <Phone className="w-3 h-3" /> Telefone *
                </Label>
                <Input
                  placeholder="(11) 99999-9999"
                  value={clientPhone}
                  onChange={e => setClientPhone(formatBrazilPhone(e.target.value))}
                  inputMode="tel"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5 text-xs font-medium">
                  <Mail className="w-3 h-3" /> E-mail
                </Label>
                <Input
                  type="email"
                  placeholder="cliente@email.com"
                  value={clientEmail}
                  onChange={e => setClientEmail(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Seção: Serviço e Profissional */}
          <div className="rounded-lg border border-border/60 bg-muted/30 p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Serviço e Profissional
            </p>

            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5 text-xs font-medium">
                <Scissors className="w-3 h-3" /> Serviço *
              </Label>
              <Select value={serviceId} onValueChange={(value) => { setServiceId(value); setProfessionalId(""); }} disabled={isPreparingOptions || hasNoServices || !!setupError}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o serviço..." />
                </SelectTrigger>
                <SelectContent>
                  {services?.map(s => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      <span className="flex items-center gap-3">
                        <span>{s.name}</span>
                        <span className="text-muted-foreground text-xs">{s.durationMinutes}min · R${Number(s.price).toFixed(2)}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedService && (
                <p className="text-xs text-muted-foreground pl-1">
                  Duração: {selectedService.durationMinutes} min · Valor: <strong>R$ {Number(selectedService.price).toFixed(2)}</strong>
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5 text-xs font-medium">
                <UserCheck className="w-3 h-3" /> Profissional habilitado *
              </Label>
              <Select value={professionalId} onValueChange={setProfessionalId} disabled={isPreparingOptions || !serviceId || compatibleProfessionalsLoading || hasNoProfessionals || hasNoEligibleProfessionals || !!setupError}>
                <SelectTrigger>
                  <SelectValue placeholder={serviceId ? "Selecione o profissional..." : "Selecione o serviço primeiro"} />
                </SelectTrigger>
                <SelectContent>
                  {eligibleProfessionals.map(p => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!serviceId ? (
                <p className="text-xs text-muted-foreground pl-1">Escolha primeiro o serviço para ver somente quem pode executá-lo.</p>
              ) : compatibleProfessionalsLoading ? (
                <p className="flex items-center gap-1 text-xs text-muted-foreground pl-1"><Loader2 className="h-3 w-3 animate-spin" />Conferindo profissionais habilitados...</p>
              ) : null}
            </div>
          </div>

          {/* Horário e observações */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2"><Label className="flex items-center gap-1.5 text-xs font-medium"><Clock className="w-3 h-3" /> Data e horário *</Label><span className="text-[11px] text-muted-foreground">Já sugerido</span></div>
              <div className="flex flex-wrap gap-1.5">
                {[{ label: "Próximo horário", minutes: 0 }, { label: "+30 min", minutes: 30 }, { label: "+1 hora", minutes: 60 }].map((option) => <Button key={option.label} type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => setStartTime(nextQuarterHour(option.minutes))}>{option.label}</Button>)}
              </div>
              <Input type="datetime-local" value={startTime} onChange={e => setStartTime(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Observações</Label>
              <Textarea placeholder="Ex: alérgico a amônia..." value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="resize-none" />
            </div>
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={requestClose} disabled={createWalkIn.isPending}>
              Cancelar
            </Button>
            <Button type="submit" disabled={createWalkIn.isPending || isPreparingOptions || compatibleProfessionalsLoading || hasNoServices || hasNoProfessionals || hasNoEligibleProfessionals || !!setupError} className="gap-2">
              <Zap className="w-4 h-4" />
              {createWalkIn.isPending ? "Criando..." : "Iniciar Atendimento"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
